/**
 * Eager GLB preload for site-planner catalog models (drei useGLTF cache).
 * Docs: docs/ADU_SITE_PLANNER.md § Model loading
 */

import { useGLTF } from '@react-three/drei';
import {
  allCatalogEntries,
  catalogGlbUrl,
  construxCatalog,
  softplanExamplesCatalog,
} from '../data/sitePlannerCatalog';

const inflight = new Map();
const ready = new Set();

export function catalogModelUrl(slug) {
  return catalogGlbUrl(slug);
}

export function isCatalogModelReady(slug) {
  return ready.has(slug);
}

/**
 * Preload one catalog slug. Resolves when the GLB is in the drei cache.
 * Safe to call repeatedly; shares in-flight promises.
 */
export function preloadCatalogModel(slug) {
  if (!slug) return Promise.resolve();
  if (ready.has(slug)) return Promise.resolve(slug);
  if (inflight.has(slug)) return inflight.get(slug);

  const url = catalogGlbUrl(slug);
  const p = Promise.resolve(useGLTF.preload(url))
    .then(() => {
      ready.add(slug);
      inflight.delete(slug);
      return slug;
    })
    .catch((err) => {
      inflight.delete(slug);
      throw err;
    });
  inflight.set(slug, p);
  return p;
}

/**
 * Background preload: Construx first, then SoftPlan examples.
 * Limited concurrency so large SoftPlan GLBs don't stall the tab.
 */
export function preloadCatalogQueue({
  concurrency = 2,
  onProgress,
  signal,
} = {}) {
  const slugs = [
    ...construxCatalog().map((m) => m.slug),
    ...softplanExamplesCatalog().map((m) => m.slug),
  ];
  const unique = [...new Set(slugs)];
  let index = 0;
  let active = 0;
  let done = 0;
  let rejected = false;

  return new Promise((resolve) => {
    const pump = () => {
      if (signal?.aborted || rejected) {
        resolve({ done, total: unique.length, aborted: true });
        return;
      }
      while (active < concurrency && index < unique.length) {
        const slug = unique[index++];
        active++;
        preloadCatalogModel(slug)
          .catch(() => {
            /* keep queue moving */
          })
          .finally(() => {
            active--;
            done++;
            onProgress?.({
              done,
              total: unique.length,
              slug,
              ready: ready.has(slug),
            });
            if (done >= unique.length) {
              resolve({ done, total: unique.length, aborted: false });
            } else {
              pump();
            }
          });
      }
      if (unique.length === 0) {
        resolve({ done: 0, total: 0, aborted: false });
      }
    };
    pump();
  });
}

export function catalogEntryCount() {
  return allCatalogEntries().length;
}
